import { useEffect, useState } from 'react';

/**
 * The design's three layouts (script: `phone = W < 720`, `tab = W < 1100`).
 * The same widths are the `tab:` and `desk:` breakpoints in styles.css; this
 * is for the few places where the layout also depends on state, like Chat.
 */
export type Viewport = 'phone' | 'tablet' | 'desktop';

export const TABLET_MIN = 720;
export const DESKTOP_MIN = 1100;

export function viewportOf(width: number): Viewport {
  if (width < TABLET_MIN) return 'phone';
  if (width < DESKTOP_MIN) return 'tablet';
  return 'desktop';
}

const QUERIES = [`(min-width: ${TABLET_MIN}px)`, `(min-width: ${DESKTOP_MIN}px)`] as const;

function current(): Viewport {
  if (typeof window === 'undefined') return 'desktop';
  return viewportOf(window.innerWidth);
}

/** The current layout, followed as the window crosses a breakpoint. */
export function useViewport(): Viewport {
  const [viewport, setViewport] = useState<Viewport>(current);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const lists = QUERIES.map((q) => window.matchMedia(q));
    const change = (): void => setViewport(current());
    change();
    for (const list of lists) list.addEventListener('change', change);
    return () => {
      for (const list of lists) list.removeEventListener('change', change);
    };
  }, []);
  return viewport;
}
